import { useEffect, useState } from 'react';
import { useStaffSchedules } from '@/hooks/useScheduling';
import { useAuth } from '@/hooks/useAuth';
import AccessDenied from '@/components/AccessDenied';
import { SCHEDULING_PERMISSIONS, hasPermission } from '@/lib/schedulingPermissions';
import type { ApiComponents } from '@jinzhuan/api-client';

type StaffScheduleDto = ApiComponents['schemas']['StaffScheduleDto'];

const STATUS_LABELS: Record<string, string> = {
  available: '可排班',
  unavailable: '不可用',
  leave: '休假',
};

// Static Tailwind class maps — avoids dynamic class construction that breaks purge
const STATUS_BADGE_STYLES: Record<string, string> = {
  available: 'bg-green-100 text-green-800',
  leave: 'bg-yellow-100 text-yellow-800',
  unavailable: 'bg-red-100 text-red-800',
};

const SLOT_STYLES = {
  available: 'text-xs px-2 py-0.5 rounded bg-green-50 text-green-700',
  unavailable: 'text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-500 line-through',
} as const;

function formatDate(date: Date): string {
  return date.toISOString().split('T')[0] ?? '';
}

export default function StaffSchedulesPage() {
  const today = formatDate(new Date());
  const [selectedDate, setSelectedDate] = useState(today);
  const { permissions } = useAuth();
  const { schedules, loading, error, fetchSchedulesByDate } = useStaffSchedules();

  // Derive permission flag — must be before conditional returns
  const canRead = hasPermission(permissions, SCHEDULING_PERMISSIONS.SCHEDULE_READ);

  useEffect(() => {
    if (!canRead) return;
    if (selectedDate) {
      void fetchSchedulesByDate(selectedDate);
    }
  }, [selectedDate, fetchSchedulesByDate, canRead]);

  // Permission early return — after all hooks
  if (!canRead) {
    return <AccessDenied resource="员工排班" />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">员工排班</h1>
      </div>

      {/* Date picker */}
      <div className="flex items-center gap-4">
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-1">选择日期</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">
          加载失败：{error.message}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">加载中...</div>
      ) : (
        <div className="rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left font-medium">员工 ID</th>
                <th className="px-4 py-3 text-left font-medium">日期</th>
                <th className="px-4 py-3 text-left font-medium">状态</th>
                <th className="px-4 py-3 text-left font-medium">时间段</th>
                <th className="px-4 py-3 text-left font-medium">备注</th>
              </tr>
            </thead>
            <tbody>
              {schedules.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                    {selectedDate} 暂无排班记录
                  </td>
                </tr>
              ) : (
                schedules.map((schedule: StaffScheduleDto) => (
                  <tr key={schedule.id} className="border-b hover:bg-muted/25">
                    <td className="px-4 py-3 font-mono text-xs">{schedule.staffId}</td>
                    <td className="px-4 py-3">{schedule.date}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_BADGE_STYLES[schedule.status] ?? 'bg-gray-100 text-gray-800'}`}
                      >
                        {STATUS_LABELS[schedule.status] ?? schedule.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {schedule.slots.map((slot, idx) => (
                          <span
                            key={idx}
                            className={slot.available ? SLOT_STYLES.available : SLOT_STYLES.unavailable}
                          >
                            {slot.start}–{slot.end}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{schedule.notes ?? '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
